// Centralized API base URL helper for CRA
import { ensureParentSession, isParentSessionRequest, parentRequestOptions, readParentSession, renewParentSession } from './parent/parentSession';
// Uses REACT_APP_API_BASE in production; falls back to localhost for local dev/tests

export function getApiBase() {
  const raw = process.env.REACT_APP_API_BASE;
  let base = undefined;
  if (typeof raw === 'string' && raw.trim() !== '') {
    base = raw.trim();
  } else if (process.env.NODE_ENV === 'production') {
    // Default to production API when building for production
    base = 'https://api.kolaykarekod.com';
  } else {
    // Local/dev default
    base = 'http://localhost:8000';
  }
  return base.replace(/\/+$/, '');
}

export function apiUrl(path = '') {
  const base = getApiBase();
  if (!path) return base;
  return `${base}${path.startsWith('/') ? '' : '/'}${path}`;
}

// In-flight request de-duplication (GET/HEAD only).
// This prevents redundant identical backend calls (commonly observed in React 18 dev StrictMode
// where effects can be invoked twice, causing the same fetch to be started twice).
//
// Important: we return `Response.clone()` for every caller so each consumer can safely call
// `.json()`/`.text()` without interfering with other consumers.
const _inflight = new Map();

function _normalizeHeaders(headers) {
  if (!headers) return {};
  try {
    if (typeof Headers !== 'undefined' && headers instanceof Headers) {
      const out = {};
      headers.forEach((v, k) => {
        out[String(k).toLowerCase()] = String(v);
      });
      return out;
    }
  } catch (_) {
    // ignore
  }

  if (Array.isArray(headers)) {
    const out = {};
    headers.forEach((pair) => {
      if (!pair || pair.length < 2) return;
      out[String(pair[0]).toLowerCase()] = String(pair[1]);
    });
    return out;
  }

  if (typeof headers === 'object') {
    const out = {};
    Object.keys(headers).forEach((k) => {
      out[String(k).toLowerCase()] = String(headers[k]);
    });
    return out;
  }

  return {};
}

function _stableStringify(value) {
  if (value === null || value === undefined) return String(value);
  const t = typeof value;
  if (t === 'string') return JSON.stringify(value);
  if (t === 'number' || t === 'boolean') return String(value);
  if (Array.isArray(value)) return `[${value.map(_stableStringify).join(',')}]`;
  if (t === 'object') {
    const keys = Object.keys(value).sort();
    return `{${keys.map((k) => `${JSON.stringify(k)}:${_stableStringify(value[k])}`).join(',')}}`;
  }
  // functions/symbols/etc should not appear in our request key; stringify defensively
  return JSON.stringify(String(value));
}

async function _fetchWithRefresh(url, opts) {
  const base = getApiBase();
  const parentRequest = isParentSessionRequest(base, url);
  if (parentRequest) await ensureParentSession(base);
  const parentAccessUsed = parentRequest ? readParentSession(base)?.access_token : null;
  let res = await fetch(url, parentRequestOptions(base, url, opts));
  if (res.status === 401) {
    if (parentRequest && readParentSession(base)) {
      if (readParentSession(base).access_token !== parentAccessUsed) {
        return fetch(url, parentRequestOptions(base, url, opts));
      }
      try {
        if (await renewParentSession(base)) return fetch(url, parentRequestOptions(base, url, opts));
      } catch (error) {
        if (error.status !== 401 && error.status !== 403) throw error;
      }
      return res;
    }
    // If the original request is a superadmin endpoint, do not auto-refresh.
    try {
      const base = getApiBase();
      const requestPath = (() => {
        if (!url) return '';
        if (url.startsWith(base)) return url.slice(base.length) || '/';
        if (url.startsWith('/')) return url;
        return '';
      })();
      if (requestPath.startsWith('/superadmin')) {
        return res;
      }
    } catch (_) {
      /* ignore path parse errors */
    }
    try {
      // Attempt a refresh
      const r = await fetch(apiUrl('/auth/refresh'), { method: 'POST', credentials: 'include' });
      if (r.ok) {
        let payload = null;
        try {
          payload = await r.clone().json();
        } catch (_) {}
        if (payload) {
          try {
            window.dispatchEvent(
              new CustomEvent('kk:auth:sessionUpdated', {
                detail: {
                  access_expires_in: payload.access_expires_in,
                  refresh_idle_in: payload.refresh_idle_in,
                  refresh_abs_in: payload.refresh_abs_in,
                  server_ts: payload.server_ts,
                  _forceAccessReset: true,
                },
              })
            );
          } catch (_) {}
        }
        res = await fetch(url, opts); // retry
      }
    } catch (_) {
      // ignore
    }
  }
  return res;
}

// Basic JSON fetch with auto-refresh on 401 once
export async function fetchJson(path, options = {}) {
  const isAbsolute = typeof path === 'string' && /^https?:\/\//i.test(path);
  const url = isAbsolute ? path : apiUrl(path);
  const opts = { credentials: 'include', ...options };

  const method = String(opts.method || 'GET').toUpperCase();
  const shouldDedupe = (method === 'GET' || method === 'HEAD') && options.dedupe !== false;
  if (!shouldDedupe) {
    return _fetchWithRefresh(url, opts);
  }

  const key = `${method} ${url} h=${_stableStringify(_normalizeHeaders(opts.headers))}`;
  const existing = _inflight.get(key);
  if (existing) {
    const res = await existing;
    return res.clone();
  }

  const p = _fetchWithRefresh(url, opts)
    .finally(() => {
      _inflight.delete(key);
    });
  _inflight.set(key, p);
  const res = await p;
  return res.clone();
}

// Explicit session refresh helper (used for manual 'extend session' actions)
export async function refreshSession() {
  try {
    const r = await fetch(apiUrl('/auth/refresh'), { method: 'POST', credentials: 'include' });
    if (!r.ok) return false;
    let payload = null;
    try { payload = await r.clone().json(); } catch (_) {}
    if (payload) {
      try {
        window.dispatchEvent(new CustomEvent('kk:auth:sessionUpdated', { detail: {
          access_expires_in: payload.access_expires_in,
          refresh_idle_in: payload.refresh_idle_in,
          refresh_abs_in: payload.refresh_abs_in,
          server_ts: payload.server_ts,
          _forceAccessReset: true,
        }}));
      } catch (_) {}
    }
    return true;
  } catch (_) {
    return false;
  }
}
